import { InMemoryCache } from '@apollo/client';
import { ISSFBehaviorsOptions } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { createApolloClient } from 'providers/ApolloProvider';
import {
  QUERY_b2bPositionsSettingsPageFetchPositionsByDepartmentId,
  QUERY_fetchCommunities,
} from 'queries';
import {
  B2bPositionsSettingsPageFetchPositionsByDepartmentIdQuery,
  B2bCustomer,
} from 'types/graphql';

import { ApplyPositionView } from 'pages/Settings/pages/Positions/B2BPositionsSettingsPage/modals/PositionsByLocation/ApplyPositionView';

export const PositionsAllocationComponentMap = {
  ApplyPositionView,
};

export type PositionsAllocationModalViewType = keyof typeof PositionsAllocationComponentMap;

export const fetchPositionsByDepartmentIdQueryConfig = (departmentId: number) => ({
  query: QUERY_b2bPositionsSettingsPageFetchPositionsByDepartmentId,
  key: `B2B_DEPARTMENTS_SETTINGS_PAGE_FETCH_POSITIONS_BY_ID_${departmentId}`,
  responseTransformer: (response: B2bPositionsSettingsPageFetchPositionsByDepartmentIdQuery) => {
    const data = response?.me as B2bCustomer;
    if (!data?.serviceablePositions) {
      return { positions: [] };
    }
    return {
      positions:
        data?.serviceablePositions
          .filter((position) => !!position.department)
          .map((position) => ({
            departmentId: `${position.department!.id}`,
            departmentName: position.department!.name,
            positionId: `${position.id}`,
            positionName: position.name,
            locationsPerPosition: position.serviceableCommunities,
            updatedAt: position.updatedAt,
            openShiftPolicy: position.openShiftPolicy,
          }))
          .filter((position) => position.departmentId === `${departmentId}`) ?? [],
    };
  },
});

const fetchCommunitiesList = createApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          fetchCommunities: {
            keyArgs: false,
            merge(existing = { edges: [] }, incoming = { edges: [] }) {
              const nextEdges = [...existing.edges];
              if (incoming.edges?.length > 0)
                incoming.edges.forEach((record: { node: { __ref: string } }) => {
                  if (record && record.node?.__ref)
                    if (
                      !nextEdges.some(
                        (_record: { node: { __ref: string } }) =>
                          record.node.__ref === _record.node.__ref,
                      )
                    )
                      nextEdges.push(record);
                });
              return { ...existing, ...incoming, edges: nextEdges };
            },
          },
        },
      },
    },
  }),
});

export const fetchCommunitiesConfig = (): ISSFBehaviorsOptions => ({
  key: 'FETCH_ALLOCATED_COMMUNITIES',
  query: QUERY_fetchCommunities,
  client: fetchCommunitiesList,
  debounceTimeout: 500,
  pageSize: 20,
  paginationType: 'InfiniteScroll',
  payloadTransformer: (payload) => {
    return {
      ...payload,
      filter: {
        ...payload.filter,
        ...(payload.byName && { byName: payload.byName }),
      },
    };
  },
  schema: {
    byName: {
      initialValue: '',
      mapper: String,
    },
  },
});
